import supabase from './supabase';
import { clearAllCaches } from '../serviceWorkerRegistration';
import { useAuthStore } from '../store/authStore';


async function refreshSession() {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    
    if (!session) {
      // Force token refresh
      const { data: refreshedSession, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) throw refreshError;
      return refreshedSession.session;
    }
    
    return session;
  } catch (error) {
    console.error('Session refresh failed:', error);
    throw error;
  }
}

export async function initializeConversation(recipientId: string, initialMessage?: string) {
  try {
    // Clear caches to ensure fresh state
    clearAllCaches();
    
    // ✅ FIXED: Get current user from auth store (user_profiles ID)
    const { user } = useAuthStore.getState();
    if (!user?.id) throw new Error('Not authenticated');
    
    const currentUserId = user.id; // This is user_profiles ID
    
    // Check if users have blocked each other
    const { data: blockData, error: blockError } = await supabase
      .from('user_blocks')
      .select('blocker_id')
      .or(`and(blocker_id.eq.${user.id},blocked_id.eq.${recipientId}),and(blocker_id.eq.${recipientId},blocked_id.eq.${user.id})`)
      .limit(1);

    if (blockError && blockError.code !== 'PGRST116') {
      throw new Error('Failed to check blocking status');
    }

    if (blockData && blockData.length > 0) {
      throw new Error('Cannot start conversation with blocked user');
    }
    
    console.log('=== CONVERSATION DEBUG ===');
    console.log('Current user ID (from auth store):', currentUserId);
    console.log('Recipient ID:', recipientId);
    
    const { data, error } = await supabase.rpc('get_or_create_conversation', {
      user1: currentUserId < recipientId ? currentUserId : recipientId,
      user2: currentUserId < recipientId ? recipientId : currentUserId,
      initial_message: initialMessage || null
    });
    
    if (error) {
      console.error('RPC error:', error);
      throw error;
    }
    
    return data;
  } catch (error: any) {
    console.error('Error initializing conversation:', error);
    
    // Handle specific error cases
    if (error.message?.includes('session_not_found')) {
      throw new Error('Your session has expired. Please sign in again.');
    }
    
    throw error;
  }
}

export async function initializeMarketplaceContact(
  recipientId: string,
  listingId: string,
  message: string
) {
  try {
    // Clear caches to ensure fresh state
    clearAllCaches();
    
    // ✅ FIXED: Get current user from auth store instead of session
    const { user } = useAuthStore.getState();
    if (!user?.id) throw new Error('Not authenticated');
    
    const currentUserId = user.id; // This is user_profiles ID
    
    // First verify the listing exists and belongs to recipient
    const { data: listing, error: listingError } = await supabase
      .from('service_listings')
      .select('id, provider_id, availability')
      .eq('id', listingId)
      .single();
    
    if (listingError || !listing) {
      throw new Error('This listing is no longer available');
    }
    
    // Verify the listing belongs to the recipient and is still available
    if (listing.provider_id !== recipientId) {
      throw new Error('Invalid listing provider');
    }
    
    if (!listing.availability) {
      throw new Error('This service is no longer available');
    }
    
    // ✅ FIXED: Use user_profiles IDs for both users
    const { data: conversationId, error: convError } = await supabase.rpc('get_or_create_conversation', {
      user1: currentUserId < recipientId ? currentUserId : recipientId,
      user2: currentUserId < recipientId ? recipientId : currentUserId
    });
    
    if (convError) throw convError;
    
    // ✅ FIXED: Use user_profiles ID for sender_id
    const { error: messageError } = await supabase
      .from('messages')
      .insert({
        conversation_id: conversationId,
        sender_id: currentUserId, // Changed from session.user.id
        content: message,
        listing_reference: listingId
      });
    
    if (messageError) throw messageError;
    
    // Update conversation last message
    await supabase
      .from('conversations')
      .update({
        last_message: message,
        last_message_at: new Date().toISOString()
      })
      .eq('id', conversationId);
    
    return conversationId;
  } catch (error: any) {
    console.error('Error initializing marketplace contact:', error);
    
    // Handle specific error cases
    if (error.message?.includes('session_not_found')) {
      throw new Error('Your session has expired. Please sign in again.');
    }
    if (error.message?.includes('Not authorized')) {
      throw new Error('You are not authorized to perform this action');
    }
    
    throw error;
  }
}